import axios from 'axios';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { postLogin, postLogout, postRequestReset } from '../../../api/account';
import { cleanCookies } from '../../../utils/cleanCookies';

import { REQUEST } from '../../../constants/request';

let isAuthenticatedStorage;
const localStorageState =
  JSON.parse(localStorage.getItem('redux_auth')) || null;
if (localStorageState) {
  isAuthenticatedStorage = localStorageState?.isAuthenticated || false;
}

export const loginUser = createAsyncThunk(
  'auth/login/loginUser',
  async (loginData, { signal, rejectWithValue }) => {
    const source = axios.CancelToken.source();
    signal.addEventListener('abort', () => {
      source.cancel();
    });
    try {
      const response = await postLogin(loginData, {
        cancelToken: source.token,
      });
      const { data, status, headers } = response;
      return { data, status, headers };
    } catch (error) {
      return rejectWithValue(error.response.status);
    }
  },
);

export const logoutUser = createAsyncThunk(
  'auth/login/logoutUser',
  async (loginData, { signal, rejectWithValue }) => {
    const source = axios.CancelToken.source();
    signal.addEventListener('abort', () => {
      source.cancel();
    });
    try {
      const response = await postLogout(loginData, {
        cancelToken: source.token,
      });
      const { data, status, headers } = response;
      return { data, status, headers };
    } catch (error) {
      return rejectWithValue(error.response.status);
    }
  },
);

export const requestResetPassword = createAsyncThunk(
  'auth/login/requestResetPassword',
  async (requestResetData, { signal, rejectWithValue }) => {
    const source = axios.CancelToken.source();
    signal.addEventListener('abort', () => {
      source.cancel();
    });
    try {
      const response = await postRequestReset(requestResetData, {
        cancelToken: source.token,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.status);
    }
  },
);

export const LoginFormSlice = createSlice({
  name: 'login',
  initialState: {
    credentials: {
      id: '',
      password: '',
      remember: false,
    },
    isAuthenticated: isAuthenticatedStorage || false,
    isRequestResetPassword: false,
    loading: REQUEST.idle,
    error: null,
    currentRequestId: undefined,
    userId: '',
  },
  reducers: {
    login: (state, action) => {
      localStorage.setItem(
        'redux_auth',
        JSON.stringify({
          isAuthenticated: true,
          userId: action.payload,
        }),
      );

      state.isAuthenticated = true;
      state.userId = action.payload;
    },
  },
  extraReducers: {
    [loginUser.pending]: (state, action) => {
      if (state.loading === REQUEST.idle) {
        state.loading = REQUEST.pending;
        state.currentRequestId = action.meta.requestId;
      }
    },
    [loginUser.fulfilled]: (state, action) => {
      const { requestId } = action.meta;
      if (
        state.loading === REQUEST.pending &&
        state.currentRequestId === requestId
      ) {
        const { remember } = action.meta.arg;
        // get userId from cookies
        const userId = document.cookie
          .match(/\bid=.\w+;/g)[0]
          .slice(3, document.cookie.length - 2);
        state.loading = REQUEST.idle;
        state.isAuthenticated = true;
        state.currentRequestId = undefined;
        state.credentials.remember = remember;
        state.userId = userId;
        state.error = null;

        localStorage.setItem(
          'redux_auth',
          JSON.stringify({
            isAuthenticated: state.isAuthenticated,
            userId,
          }),
        );
      }
    },
    [loginUser.rejected]: (state, action) => {
      const { requestId } = action.meta;
      if (
        state.loading === REQUEST.pending &&
        state.currentRequestId === requestId
      ) {
        state.loading = REQUEST.idle;
        state.error = action.payload;
        state.currentRequestId = undefined;
      }
    },
    [logoutUser.pending]: (state, action) => {
      if (state.loading === REQUEST.idle) {
        state.loading = REQUEST.pending;
        state.currentRequestId = action.meta.requestId;
      }
    },
    [logoutUser.fulfilled]: (state, action) => {
      const { requestId } = action.meta;
      if (
        state.loading === REQUEST.pending &&
        state.currentRequestId === requestId
      ) {
        state.loading = REQUEST.idle;
        state.isAuthenticated = false;
        state.currentRequestId = undefined;
        state.credentials.id = '';
        state.credentials.password = '';
        state.credentials.remember = false;
        state.userId = '';
        localStorage.clear();
        cleanCookies();
      }
    },
    [logoutUser.rejected]: (state, action) => {
      const { requestId } = action.meta;
      if (
        state.loading === REQUEST.pending &&
        state.currentRequestId === requestId
      ) {
        state.loading = REQUEST.idle;
        state.error = action.payload;
        state.currentRequestId = undefined;
      }
    },
    [requestResetPassword.pending]: (state, { meta }) => {
      if (state.loading === REQUEST.idle) {
        state.loading = REQUEST.pending;
        state.currentRequestId = meta.requestId;
      }
    },
    [requestResetPassword.fulfilled]: (state, { meta }) => {
      if (
        state.loading === REQUEST.pending &&
        state.currentRequestId === meta.requestId
      ) {
        state.isRequestResetPassword = true;

        state.loading = REQUEST.idle;
        state.currentRequestId = undefined;
        state.error = null;
      }
    },
    [requestResetPassword.rejected]: (state, { payload, meta }) => {
      if (
        state.loading === REQUEST.pending &&
        state.currentRequestId === meta.requestId
      ) {
        state.loading = REQUEST.idle;
        state.error = payload;
        state.currentRequestId = undefined;
      }
    },
  },
});

export const { login } = LoginFormSlice.actions;

export default LoginFormSlice.reducer;

export const selectorIsAuthenticated = state =>
  state.auth.login.isAuthenticated;
export const selectorIsRequestResetPassword = state =>
  state.auth.login.isRequestResetPassword;
export const selectorLoginError = state => state.auth.login.error;
